import { Segment } from '@mairistem/composants';
import { ListeTable, ListeTableActionCsv, ListeTableActionPrint } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React, { type ReactNode } from 'react';
import { ModalRecu } from '@/components/Encaissement/ModalRecu';
import { MODES_REGLEMENTS } from '@/constants';
import { RegieContext } from '@/contexts';
import { useReglementPointage } from '@/hooks';
import { getRecu } from '@/routes';
import type { ModeReglementsPointageType, RecuEncaissementType, ReglementType } from '@/types';
import { formatMonetaire } from '@/utils';
import {
  ReglementBonCAFListeTableHeader,
  ReglementBonCAFListeTableRow,
} from './ReglementBonCAFListe';
import {
  ReglementBonCCASListeTableHeader,
  ReglementBonCCASListeTableRow,
} from './ReglementBonCCASListe';
import {
  ReglementCarteBancaireInternetListeTableHeader,
  ReglementCarteBancaireInternetListeTableRow,
} from './ReglementCarteBancaireInternetListe';
import {
  ReglementCarteBancaireListeTableHeader,
  ReglementCarteBancaireListeTableRow,
} from './ReglementCarteBancaireListe';
import { ReglementCESUListeTableHeader, ReglementCESUListeTableRow } from './ReglementCESUListe';
import {
  ReglementChequeListeTableHeader,
  ReglementChequeListeTableRow,
} from './ReglementChequeListe';
import {
  ReglementChequeVacanceListeTableHeader,
  ReglementChequeVacanceListeTableRow,
} from './ReglementChequeVacanceListe';
import {
  ReglementNumeraireListeTableHeader,
  ReglementNumeraireListeTableRow,
} from './ReglementNumeraireListe';
import {
  ReglementPrelevementInternetListeTableHeader,
  ReglementPrelevementInternetListeTableRow,
} from './ReglementPrelevementInternetListe';
import {
  ReglementPrelevementListeTableHeader,
  ReglementPrelevementListeTableRow,
} from './ReglementPrelevementListe';
import {
  ReglementTicketListeTableHeader,
  ReglementTicketListeTableRow,
} from './ReglementTicketListe';
import {
  ReglementVirementListeTableHeader,
  ReglementVirementListeTableRow,
} from './ReglementVirementListe';

type ReglementChequeListeProps = {
  onSelect?: (selected: ReglementType, selectedItems: ReglementType[]) => void;
  onRecuAnnulation: () => void;
  selectedItems?: ReglementType[];
  /** Défini les colonnes affichées */
  type?: ModeReglementsPointageType;
};

const ReglementListe = (props: ReglementChequeListeProps) => {
  const { onSelect, onRecuAnnulation, selectedItems, type = MODES_REGLEMENTS.CHEQUE } = props;

  const [modalRecuOpen, setModalRecuOpen] = React.useState<number | null>(null);
  const [selectedRecu, setSelectedRecu] = React.useState<RecuEncaissementType | null>(null);
  const [loadingRecu, setLoadingRecu] = React.useState<boolean>(false);

  const { total, loading, reglements, setReglements, setSorts, setPage } =
    useReglementPointage(type);

  const { regies, selectedRegie } = React.useContext(RegieContext);

  const montantTotalNonAnnule = React.useMemo<number>(
    () =>
      _.sumBy(
        _.filter(selectedItems, (reglement) => _.isNil(reglement.dateAnnulationRecu)),
        'montant',
      ),
    [selectedItems],
  );

  const montantTotalAnnule = React.useMemo<number>(
    () =>
      _.sumBy(
        _.filter(selectedItems, (reglement) => !_.isNil(reglement.dateAnnulationRecu)),
        'montant',
      ),
    [selectedItems],
  );

  const hasRecuParMail = React.useMemo<boolean>(
    () => _.find(regies, { identifiant: selectedRegie })?.envoiRecuParMail || false,
    [selectedRegie, regies],
  );

  // Déclenche l'ouverture de la modal reçu
  const handleModalRecuOpen = React.useCallback((identifiant: number) => {
    setModalRecuOpen(identifiant);
  }, []);

  // Ferme la modal reçu
  const handleModalRecuClose = React.useCallback(() => {
    setSelectedRecu(null);
    setModalRecuOpen(null);
  }, []);

  const handleAnnulation = React.useCallback(
    (recuAnnule: RecuEncaissementType) => {
      setSelectedRecu(recuAnnule);
      // On doit refresh toutes les listes derrière pour que le reçu apparaisse rouge là où y'a besoin
      onRecuAnnulation();
    },
    [onRecuAnnulation],
  );

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort)
          ? // tri par défaut
            null
          : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
      setReglements([]);
    },
    [setReglements, setSorts],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const getCSVColumnsByType = React.useCallback(() => {
    switch (type) {
      case MODES_REGLEMENTS.CHEQUE:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'numero', 'banque', 'tireur', 'montant'];
      case MODES_REGLEMENTS.NUMERAIRE:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'montant'];
      case MODES_REGLEMENTS.CESU:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'nombre', 'dateValidite', 'montant'];
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'nombre', 'dateValidite', 'montant'];
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'numero', 'montant'];
      case MODES_REGLEMENTS.VIREMENT:
        return ['numeroRecu', 'dateRecu', 'montant'];
      case MODES_REGLEMENTS.PRELEVEMENT:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'tireur', 'datePrelevement', 'montant'];
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'montant'];
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'montant'];
      case MODES_REGLEMENTS.TICKET:
        return [
          'numeroRecu',
          'dateRecu',
          'nomPayeur',
          'type',
          'nombre',
          'montantUnitaire',
          'dateValidite',
          'montant',
        ];
      case MODES_REGLEMENTS.BON_CCAS:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'numero', 'dateValidite', 'montant'];
      case MODES_REGLEMENTS.BON_CAF:
        return ['numeroRecu', 'dateRecu', 'nomPayeur', 'nombre', 'dateValidite', 'montant'];
      default:
        return [];
    }
  }, [type]);

  const Row = React.useMemo(() => {
    switch (type) {
      case MODES_REGLEMENTS.CHEQUE:
        return ReglementChequeListeTableRow;
      case MODES_REGLEMENTS.NUMERAIRE:
        return ReglementNumeraireListeTableRow;
      case MODES_REGLEMENTS.CESU:
        return ReglementCESUListeTableRow;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return ReglementChequeVacanceListeTableRow;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return ReglementCarteBancaireListeTableRow;
      case MODES_REGLEMENTS.VIREMENT:
        return ReglementVirementListeTableRow;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return ReglementPrelevementListeTableRow;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return ReglementCarteBancaireInternetListeTableRow;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return ReglementPrelevementInternetListeTableRow;
      case MODES_REGLEMENTS.TICKET:
        return ReglementTicketListeTableRow;
      case MODES_REGLEMENTS.BON_CCAS:
        return ReglementBonCCASListeTableRow;
      case MODES_REGLEMENTS.BON_CAF:
        return ReglementBonCAFListeTableRow;
      default:
        return null;
    }
  }, [type]);

  const Header = React.useMemo(() => {
    switch (type) {
      case MODES_REGLEMENTS.CHEQUE:
        return ReglementChequeListeTableHeader;
      case MODES_REGLEMENTS.NUMERAIRE:
        return ReglementNumeraireListeTableHeader;
      case MODES_REGLEMENTS.CESU:
        return ReglementCESUListeTableHeader;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return ReglementChequeVacanceListeTableHeader;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return ReglementCarteBancaireListeTableHeader;
      case MODES_REGLEMENTS.VIREMENT:
        return ReglementVirementListeTableHeader;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return ReglementPrelevementListeTableHeader;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return ReglementCarteBancaireInternetListeTableHeader;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return ReglementPrelevementInternetListeTableHeader;
      case MODES_REGLEMENTS.TICKET:
        return ReglementTicketListeTableHeader;
      case MODES_REGLEMENTS.BON_CCAS:
        return ReglementBonCCASListeTableHeader;
      case MODES_REGLEMENTS.BON_CAF:
        return ReglementBonCAFListeTableHeader;
      default:
        return null;
    }
  }, [type]);

  const renderRow = (item, __, rowProps) => (
    <Row
      item={item}
      onRecuOpen={handleModalRecuOpen}
      rowProps={rowProps}
      loadingRecu={loadingRecu}
      selectedRecu={modalRecuOpen}
    />
  );

  // Récupère le détail du reçu sélectionné au besoin
  React.useEffect(() => {
    if (modalRecuOpen) {
      setLoadingRecu(true);

      request
        .get(getRecu(modalRecuOpen))
        .then(setSelectedRecu)
        .finally(() => setLoadingRecu(false));
    }
  }, [modalRecuOpen]);

  return (
    <Segment
      basic
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
        alignItems: 'flex-end',
        marginBottom: '1em',
      }}
    >
      <div style={{ height: 450 }}>
        <ListeTable
          key={type}
          count={_.isEmpty(reglements) ? 0 : total}
          loading={loading}
          items={reglements}
          onSort={handleSort}
          // Séléction jusqu'à 200 éléments
          // quand on clique sur "sélectionner tout"
          // donc faut précharger les 200 premiers quoi qu'il arrive
          perPage={1000}
          onLoad={handleLoad}
          onSelect={onSelect}
          selection="checkbox"
          selectedItems={selectedItems}
        >
          <ListeTable.Header>
            <Header />
          </ListeTable.Header>
          {renderRow as unknown as ReactNode}
          <ListeTable.Actions>
            <ListeTableActionCsv
              items={reglements}
              columns={getCSVColumnsByType()}
              options={{ filename: 'Liste des règlements a contrôler' }}
            />
            <ListeTableActionPrint />
          </ListeTable.Actions>
        </ListeTable>
      </div>
      {!_.isEmpty(selectedItems) && (
        <span
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5em',
            marginRight: '0.5em',
          }}
        >
          Montant des règlements sélectionnés :
          <b>
            {/* Ne doit pas prendre en compte les règlements faisant parti de reçu annulé */}
            {formatMonetaire(montantTotalNonAnnule, 2, true)}
          </b>
          {montantTotalAnnule > 0 && (
            <span style={{ color: 'red', fontStyle: 'italic' }}>
              (montant des annulations : <b>{formatMonetaire(montantTotalAnnule, 2, true)}</b>)
            </span>
          )}
        </span>
      )}
      {modalRecuOpen && !_.isNil(selectedRecu) && (
        <ModalRecu
          recu={selectedRecu}
          envoiRecuParMail={hasRecuParMail}
          onClose={handleModalRecuClose}
          onAnnulation={handleAnnulation}
        />
      )}
    </Segment>
  );
};

export default ReglementListe;
